/**
 * ResponseIntegration - 回應系統與遊戲的整合層
 * 處理施放法術時的回應階段流程
 */

import type { GameState, CardDefinition } from '../../types';
import { useResponseStore } from '../../store/responseStore';
import { useGameStore } from '../../store/gameStore';
import type { ImmediateAction } from '../effects/EffectTriggerSystem';
import { getLogger } from '../../utils/logger';

const log = getLogger('ResponseIntegration');

// ============================================================
// 類型定義
// ============================================================

export interface SpellCastContext {
  cardId: string;
  cardName: string;
  effectText: string;
  casterId: string;
  targets?: string[];
}

export interface ResponseIntegrationConfig {
  /** 是否啟用回應階段（設為 false 可跳過） */
  enableResponsePhase: boolean;
  /** AI 玩家 ID（用於 vs AI 模式） */
  aiPlayerId: string | null;
  /** 本地玩家 ID */
  localPlayerId: string;
}

// ============================================================
// 初始化
// ============================================================

let integrationConfig: ResponseIntegrationConfig = {
  enableResponsePhase: true,
  aiPlayerId: null,
  localPlayerId: '',
};

/**
 * 初始化回應系統整合
 */
export function initializeResponseIntegration(
  cardDefinitions: Map<string, CardDefinition>,
  localPlayerId: string,
  aiPlayerId: string | null,
  enableResponsePhase: boolean = true
): void {
  integrationConfig = {
    enableResponsePhase,
    aiPlayerId,
    localPlayerId,
  };

  if (enableResponsePhase && aiPlayerId) {
    const { initialize } = useResponseStore.getState();
    initialize(cardDefinitions, localPlayerId, aiPlayerId);
    log.debug('初始化完成', { localPlayerId, aiPlayerId });
  }
}

/**
 * 更新卡牌定義（當載入新卡牌時）
 */
export function updateResponseDefinitions(
  cardDefinitions: Map<string, CardDefinition>
): void {
  const { controller, aiDecider } = useResponseStore.getState();
  if (controller) {
    controller.setCardDefinitions(cardDefinitions);
  }
  if (aiDecider) {
    aiDecider.setCardDefinitions(cardDefinitions);
  }
}

// ============================================================
// 法術施放流程
// ============================================================

/**
 * 帶回應階段的法術施放
 * 這是主要的整合點，UI 應該呼叫這個函數而非直接呼叫 castSpell
 */
export async function castSpellWithResponse(
  cardId: string,
  targets?: string[]
): Promise<{ success: boolean; error?: string; wasCountered?: boolean }> {
  const gameStore = useGameStore.getState();
  const responseStore = useResponseStore.getState();
  const { gameState, cardDefinitions } = gameStore;

  if (!gameState) {
    return { success: false, error: '遊戲未初始化' };
  }

  // 找到要施放的卡牌
  const activePlayerId = gameState.activePlayerId;
  const activePlayer = activePlayerId === gameState.player1.id 
    ? gameState.player1 
    : gameState.player2;
  const card = activePlayer.hand.find(c => c.id === cardId);

  if (!card) {
    return { success: false, error: '手牌中找不到該卡牌' };
  }

  const cardDef = cardDefinitions.get(card.definitionId);
  if (!cardDef || cardDef.type !== 'spell') {
    return { success: false, error: '該卡牌不是法術卡' };
  }

  // 如果不啟用回應階段，直接施放
  if (!integrationConfig.enableResponsePhase || !integrationConfig.aiPlayerId) {
    const result = gameStore.castSpell(cardId, targets);
    return { ...result, wasCountered: false };
  }

  log.debug('開始法術施放流程:', cardDef.nameZh || cardDef.name);

  // 開始回應階段
  await responseStore.startResponsePhase(
    gameState,
    'spell_cast',
    activePlayerId,
    cardId,
    cardDef.nameZh || cardDef.name || cardId,
    cardDef.effectText || '',
    [], // actions 暫時為空，實際效果由 effectTriggerSystem 處理
    targets
  );

  // 取得最新的遊戲狀態
  const currentGameState = useGameStore.getState().gameState;
  if (!currentGameState) {
    return { success: false, error: '遊戲狀態遺失' };
  }

  // 檢查連鎖是否需要結算
  const chainManager = responseStore.controller?.getChainManager();
  if (chainManager?.hasPendingChain()) {
    // 有連鎖待結算
    log.debug('結算連鎖...');
    
    // 結算連鎖（這會處理無效化等效果）
    const finalState = responseStore.resolveChain(
      currentGameState,
      (state, action) => executeImmediateAction(state, action)
    );

    // 檢查法術是否被無效化
    const chainState = chainManager.getChainState();
    const wasCountered = chainState.entries.some(
      e => e.sourceCardId === cardId && e.status === 'negated'
    );

    if (wasCountered) {
      log.debug('法術被無效化');
      
      // 法術被無效，只從手牌移除到墓地，不觸發效果
      const updatedPlayer = {
        ...activePlayer,
        hand: activePlayer.hand.filter(c => c.id !== cardId),
        graveyard: [...activePlayer.graveyard, { ...card, location: 'graveyard' as const }],
      };

      const newState = {
        ...finalState,
        [activePlayerId === currentGameState.player1.id ? 'player1' : 'player2']: updatedPlayer,
      };

      useGameStore.setState({ gameState: newState });
      return { success: true, wasCountered: true };
    }
  }

  // 正常施放法術
  const result = gameStore.castSpell(cardId, targets);
  return { ...result, wasCountered: false };
}

/**
 * 帶回應階段的單位召喚
 */
export async function summonWithResponse(
  cardId: string,
  position: { side: 'player1' | 'player2'; row: 'front' | 'back'; column: 1 | 2 | 3 | 4 | 5 }
): Promise<{ success: boolean; error?: string; wasCountered?: boolean }> {
  const gameStore = useGameStore.getState();
  const responseStore = useResponseStore.getState();
  const { gameState, cardDefinitions } = gameStore;

  if (!gameState) {
    return { success: false, error: '遊戲未初始化' };
  }

  // 找到要召喚的卡牌
  const activePlayerId = gameState.activePlayerId;
  const activePlayer = activePlayerId === gameState.player1.id 
    ? gameState.player1 
    : gameState.player2;
  const card = activePlayer.hand.find(c => c.id === cardId);

  if (!card) {
    return { success: false, error: '手牌中找不到該卡牌' };
  }

  const cardDef = cardDefinitions.get(card.definitionId);
  if (!cardDef || cardDef.type !== 'unit') {
    return { success: false, error: '該卡牌不是單位卡' };
  }

  // 如果不啟用回應階段，直接召喚
  if (!integrationConfig.enableResponsePhase || !integrationConfig.aiPlayerId) {
    const result = gameStore.turnExecuteSummon(cardId, position);
    return { ...result, wasCountered: false };
  }

  // 開始回應階段（對手可以回應「敵方打出單位時」）
  await responseStore.startResponsePhase(
    gameState,
    'unit_summon',
    activePlayerId,
    cardId,
    cardDef.nameZh || cardDef.name || cardId,
    cardDef.effectText || '',
    [],
    undefined
  );

  // 取得最新狀態
  const currentGameState = useGameStore.getState().gameState;
  if (!currentGameState) {
    return { success: false, error: '遊戲狀態遺失' };
  }

  // 結算連鎖
  const chainManager = responseStore.controller?.getChainManager();
  if (chainManager?.hasPendingChain()) {
    responseStore.resolveChain(
      currentGameState,
      (state, action) => executeImmediateAction(state, action)
    );
  }

  // 正常召喚
  const result = gameStore.turnExecuteSummon(cardId, position);
  return { ...result, wasCountered: false };
}

// ============================================================
// 輔助函數
// ============================================================

/**
 * 執行即時行動（用於連鎖結算）
 */
function executeImmediateAction(
  state: GameState,
  action: ImmediateAction
): GameState {
  // TODO: 整合 ActionExecutor 來執行效果
  // 目前先返回原狀態，待整合 ActionExecutor
  log.debug('執行行動:', action);
  return state;
}

// ============================================================
// React Hook
// ============================================================

/**
 * useResponseIntegration - 整合用 Hook
 */
export function useResponseIntegration() {
  return {
    castSpellWithResponse,
    summonWithResponse,
    initializeResponseIntegration,
    updateResponseDefinitions,
  };
}
